import type {
  EmploymentType,
  SeniorityLevel,
  UserRole,
  WorkArrangement,
  WorkAuthorization,
} from '../types/core';
import type {
  ApplicationStatus,
  MandateStage,
  OpportunityStatus,
  PresentationStatus,
} from '../types/search';
import type {
  ArticleCategory,
} from '../types/content';
import type {
  AuditAction,
  DocumentCategory,
  DocumentReviewStatus,
  FeedbackIssueCategory,
  NotificationType,
  PrivacyRequestKind,
  ServiceOrderStatus,
  TicketQueue,
} from '../types/platform';

export const roleLabels: Record<UserRole, string> = {
  candidate: 'Candidate',
  employer: 'Employer',
  recruiter: 'Recruiter',
  administrator: 'Administrator',
  super_administrator: 'Super Administrator',
};

export const seniorityLabels: Record<SeniorityLevel, string> = {
  board_director: 'Board Director',
  chief_executive: 'Chief Executive',
  c_suite: 'C-Suite',
  evp: 'Executive Vice President',
  svp: 'Senior Vice President',
  vice_president: 'Vice President',
  senior_director: 'Senior Director',
};

export const workArrangementLabels: Record<WorkArrangement, string> = {
  onsite: 'Onsite',
  hybrid: 'Hybrid',
  remote: 'Remote',
};

export const employmentTypeLabels: Record<EmploymentType, string> = {
  permanent: 'Permanent',
  fixed_term: 'Fixed term',
  interim: 'Interim',
  fractional: 'Fractional',
  board_appointment: 'Board appointment',
};

export const workAuthorizationLabels: Record<WorkAuthorization, string> = {
  us_citizen_or_permanent_resident: 'U.S. citizen or permanent resident',
  authorized_no_sponsorship_required: 'Authorized to work, no sponsorship required',
  sponsorship_required: 'Sponsorship required',
  authorized_outside_us: 'Authorized to work outside the United States',
};

export const opportunityStatusLabels: Record<OpportunityStatus, string> = {
  verified: 'Verified',
  confidential_mandate: 'Confidential Mandate',
  proposed_opportunity: 'Proposed Opportunity',
  market_matched_opportunity: 'Market-Matched Opportunity',
};

export const opportunityStatusMeaning: Record<OpportunityStatus, string> = {
  verified:
    'A commissioned mandate with a confirmed vacancy. The client, budget and reporting line have been evidenced by our research team.',
  confidential_mandate:
    'A commissioned mandate with a confirmed vacancy where the client has asked that its identity be withheld until a candidate advances.',
  proposed_opportunity:
    'A leadership requirement under discussion with an organization. No vacancy is confirmed and no mandate has yet been commissioned.',
  market_matched_opportunity:
    'A role category identified through market research rather than a client instruction. Registering interest records consent to be considered if a mandate follows.',
};

export const applicationStatusLabels: Record<ApplicationStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_preliminary_review: 'Under preliminary review',
  additional_information_requested: 'Additional information requested',
  search_team_review: 'Search-team review',
  client_authorized_submission_pending: 'Client-authorized submission pending',
  candidate_consent_required: 'Candidate consent required',
  presented_to_client: 'Presented to client',
  client_review: 'Client review',
  interview_stage: 'Interview stage',
  on_hold: 'On hold',
  not_selected: 'Not selected',
  withdrawn: 'Withdrawn',
  offer_stage: 'Offer stage',
  search_completed: 'Search completed',
};

export const applicationStatusOrder: ApplicationStatus[] = [
  'draft',
  'submitted',
  'under_preliminary_review',
  'additional_information_requested',
  'search_team_review',
  'client_authorized_submission_pending',
  'candidate_consent_required',
  'presented_to_client',
  'client_review',
  'interview_stage',
  'on_hold',
  'not_selected',
  'withdrawn',
  'offer_stage',
  'search_completed',
];

export const mandateStageLabels: Record<MandateStage, string> = {
  inquiry_received: 'Inquiry received',
  scoping: 'Scoping',
  engaged: 'Engaged',
  research_and_mapping: 'Research and mapping',
  longlist: 'Longlist',
  shortlist: 'Shortlist',
  client_interviews: 'Client interviews',
  final_stage: 'Final stage',
  offer: 'Offer',
  placed: 'Placed',
  closed_unfilled: 'Closed, unfilled',
};

export const presentationStatusLabels: Record<PresentationStatus, string> = {
  authorization_requested: 'Authorization requested',
  authorized: 'Authorized by candidate',
  declined_by_candidate: 'Declined by candidate',
  presented: 'Presented to client',
  withdrawn: 'Withdrawn',
};

export const documentCategoryLabels: Record<DocumentCategory, string> = {
  executive_resume: 'Executive résumé or CV',
  executive_biography: 'Executive biography',
  board_resume: 'Board résumé',
  board_value_proposition: 'Board Value Proposition',
  leadership_philosophy_statement: 'Leadership Philosophy Statement',
  executive_career_positioning_brief: 'Executive Career Positioning Brief',
  selective_representation_brief: 'Selective Representation Brief',
  cover_letter: 'Cover letter',
  professional_references: 'Professional references',
  certifications: 'Certifications',
  work_samples: 'Work samples',
};

export const documentReviewStatusLabels: Record<DocumentReviewStatus, string> = {
  not_submitted: 'Not submitted',
  submitted: 'Submitted',
  under_review: 'Under review',
  accepted_for_current_stage: 'Accepted for current stage',
  revision_requested: 'Revision requested',
  updated_version_received: 'Updated version received',
  not_required: 'Not required',
  archived: 'Archived',
};

export const feedbackIssueCategoryLabels: Record<FeedbackIssueCategory, string> = {
  positioning: 'Positioning',
  evidence_of_impact: 'Evidence of impact',
  structure_and_length: 'Structure and length',
  board_readiness: 'Board readiness',
  clarity_of_scope: 'Clarity of scope',
  completeness: 'Completeness',
};

export const notificationTypeLabels: Record<NotificationType, string> = {
  registration_confirmation: 'Registration confirmation',
  email_verification: 'Email verification',
  password_reset: 'Password reset',
  document_received: 'Document received',
  document_review_completed: 'Document review completed',
  revision_requested: 'Revision requested',
  opportunity_match: 'Opportunity match',
  application_received: 'Application received',
  status_update: 'Status update',
  consent_request: 'Consent request',
  interview_request: 'Interview request',
  new_message: 'New message',
  privacy_request_confirmation: 'Privacy request confirmation',
};

export const articleCategoryLabels: Record<ArticleCategory, string> = {
  executive_hiring: 'Executive hiring',
  board_leadership: 'Board leadership',
  succession_planning: 'Succession planning',
  leadership_assessment: 'Leadership assessment',
  healthcare_leadership: 'Healthcare leadership',
  technology_and_ai_leadership: 'Technology and AI leadership',
  private_equity: 'Private equity',
  compensation_trends: 'Compensation trends',
  candidate_guidance: 'Candidate guidance',
  market_intelligence: 'Market intelligence',
};

export const ticketQueueLabels: Record<TicketQueue, string> = {
  general_inquiry: 'General inquiry',
  candidate_support: 'Candidate support',
  employer_search_inquiry: 'Employer search inquiry',
  privacy_request: 'Privacy request',
  technical_support: 'Technical support',
  service_inquiry: 'Career-services inquiry',
};

export const privacyRequestKindLabels: Record<PrivacyRequestKind, string> = {
  data_download: 'Data download',
  account_deletion: 'Account deletion',
  correction: 'Correction',
  consent_withdrawal: 'Consent withdrawal',
  processing_restriction: 'Processing restriction',
};

export const serviceOrderStatusLabels: Record<ServiceOrderStatus, string> = {
  consultation_requested: 'Consultation requested',
  scope_agreed: 'Scope agreed',
  in_progress: 'In progress',
  draft_delivered: 'Draft delivered',
  revision_in_progress: 'Revision in progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

export const auditActionLabels: Record<AuditAction, string> = {
  'user.sign_in': 'Sign in',
  'user.sign_out': 'Sign out',
  'user.created': 'User created',
  'user.role_changed': 'Role changed',
  'employer.verified': 'Employer verified',
  'opportunity.verified': 'Opportunity verified',
  'opportunity.published': 'Opportunity published',
  'application.status_changed': 'Application status changed',
  'application.adverse_decision_recorded': 'Adverse decision recorded',
  'document.uploaded': 'Document uploaded',
  'document.reviewed': 'Document reviewed',
  'consent.recorded': 'Consent recorded',
  'presentation.authorization_requested': 'Authorization requested',
  'presentation.authorized': 'Presentation authorized',
  'presentation.declined': 'Presentation declined',
  'presentation.sent_to_client': 'Presented to client',
  'content.published': 'Content published',
  'privacy.request_received': 'Privacy request received',
  'privacy.request_completed': 'Privacy request completed',
  'notification.sent': 'Notification sent',
};
